import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AlarmClock, BookOpen, CheckCircle2, Clock, Eye } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCourse } from '@/hooks/useCourses'
import { useQuizzes, useStartQuizAttempt, useLearnerQuizAttempts } from '@/hooks/useQuizzes'
import { QuizModel } from '@/models/quiz.model'
import { useAuth } from '@/contexts/AuthContext'
import { paths } from '@/routes/path'
import { labelForQuizAttemptStatus } from '@/utils/quiz'
import BackButton from '@/components/ui/BackButton'

export function CourseQuizListPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  const { data: course, isLoading: isCourseLoading } = useCourse(courseId)
  const { data: quizzesData, isLoading: isQuizzesLoading } = useQuizzes(
    useMemo(
      () => ({
        courseId,
        isActive: true,
        perPage: 100,
      }),
      [courseId],
    ),
  )

  const [quizToStart, setQuizToStart] = useState<QuizModel | null>(null)
  const startAttempt = useStartQuizAttempt(quizToStart?.id)

  const quizzes = quizzesData?.quizzes ?? []

  const handleConfirmStart = async () => {
    if (!quizToStart || !courseId) return
    try {
      const result = await startAttempt.mutateAsync({})
      toast.info('Quiz attempt started. Good luck!')
      navigate(paths.app.quizzes.take.build(courseId, quizToStart.id, result.attempt.id), {
        state: result,
      })
    } catch (error) {
      console.error(error)
      toast.error('We could not start the quiz. Please try again.')
    } finally {
      setQuizToStart(null)
    }
  }

  if (isCourseLoading || isQuizzesLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!courseId || !course) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Course not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not load the quizzes for this course. Please return to your course list.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (!quizzes.length) {
    return (
      <div className="space-y-6">
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <BackButton/>
            <p className="text-sm font-medium text-red-600">Course quizzes</p>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{course.title ?? 'Course quizzes'}</h1>
          <p className="text-gray-600">
            No quizzes are currently available for this course. Check back later or contact your instructor.
          </p>
        </header>
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <AlarmClock className="h-12 w-12 text-gray-300" />
            <p className="text-lg font-semibold text-gray-700">No quizzes yet</p>
            <p className="max-w-md text-sm text-gray-500">
              When your instructor publishes a quiz, it will appear here with all the details you need to get started.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <BackButton />
          <p className="text-sm font-medium text-red-600">Course quizzes</p>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{course.title ?? 'Course quizzes'}</h1>
        <p className="text-gray-600">
          Explore available quizzes, check due dates, and track your attempts and scores. Each quiz has friendly instructions to help you prepare.
        </p>
      </header>

      <div className="space-y-4">
        {quizzes.map((quiz) => (
          <LearnerQuizCard
            key={quiz.id}
            quiz={quiz}
            courseId={courseId}
            learnerId={user?.id ?? ''}
            onStart={() => setQuizToStart(quiz)}
            onViewAttempt={(attemptId) =>
              navigate(paths.app.quizzes.learnerAttemptDetail.build(courseId, quiz.id, attemptId), {
                state: { from: location.pathname },
              })
            }
            onContinueAttempt={(attemptId) =>
              navigate(paths.app.quizzes.take.build(courseId, quiz.id, attemptId))
            }
          />
        ))}
      </div>

      <ConfirmDialog
        open={Boolean(quizToStart)}
        title="Ready to begin?"
        description={
          quizToStart
            ? `Starting "${quizToStart.title}" will begin your attempt immediately${
                quizToStart.timeLimitSeconds
                  ? ` and activate a ${Math.round(quizToStart.timeLimitSeconds / 60)} minute timer.`
                  : '.'
              } Make sure you have a stable connection before you continue.`
            : ''
        }
        confirmLabel="Start quiz"
        confirmTone="default"
        onCancel={() => setQuizToStart(null)}
        onConfirm={handleConfirmStart}
      />
    </div>
  )
}

interface LearnerQuizCardProps {
  quiz: QuizModel
  courseId: string
  learnerId: string
  onStart: () => void
  onViewAttempt: (attemptId: string) => void
  onContinueAttempt: (attemptId: string) => void
}

function LearnerQuizCard({
  quiz,
  courseId,
  learnerId,
  onStart,
  onViewAttempt,
  onContinueAttempt,
}: LearnerQuizCardProps) {
  const { data, isLoading } = useLearnerQuizAttempts(quiz.id, learnerId)

  const attempts = data?.attempts ?? []
  const inProgress = attempts.find((attempt) => attempt.status === 'in_progress')
  const gradedAttempts = attempts.filter((attempt) => attempt.status === 'graded')
  const dueDateText = quiz.dueDate ? new Date(quiz.dueDate).toLocaleString() : 'No due date'
  const timeLimitMinutes = quiz.timeLimitSeconds
    ? Math.round(quiz.timeLimitSeconds / 60)
    : null
  const hasLimitedAttempts = typeof quiz.maxAttempts === 'number' && quiz.maxAttempts > 0
  const maxAttemptsReached =
    hasLimitedAttempts && attempts.length >= (quiz.maxAttempts ?? 0)

  return (
    <Card className="border border-gray-200">
      <CardContent className="space-y-4 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{quiz.title}</h2>
            <p className="text-sm text-gray-600">
              {quiz.description ?? 'No description yet. Check the course feed for announcements.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {quiz.maxAttempts && (
              <Badge variant="outline">{quiz.maxAttempts} attempt{quiz.maxAttempts > 1 ? 's' : ''}</Badge>
            )}
            {quiz.requireProctoring && <Badge variant="secondary">Supervised</Badge>}
            {timeLimitMinutes ? (
              <Badge variant="outline">
                <Clock className="mr-1 h-3 w-3" />
                {timeLimitMinutes} min
              </Badge>
            ) : (
              <Badge variant="outline">No timer</Badge>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-400" />
            Due date: {dueDateText}
          </span>
          <span className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-gray-400" />
            {attempts.length} attempt{attempts.length === 1 ? '' : 's'} recorded
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {inProgress ? (
            <Button variant="default" onClick={() => onContinueAttempt(inProgress.id)}>
              Continue
            </Button>
          ) : (
            !maxAttemptsReached && <Button onClick={onStart}>Start quiz</Button>
          )}
          {!inProgress && maxAttemptsReached && (
            <Badge variant="outline" className="text-xs uppercase tracking-wide">
              Max attempts reached
            </Badge>
          )}
          {gradedAttempts.length > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              {gradedAttempts.length} graded
            </Badge>
          )}
        </div>

        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
          <p className="text-sm font-medium text-gray-700">Your attempts</p>
          {isLoading ? (
            <div className="flex items-center gap-2 pt-3 text-sm text-gray-500">
              <LoadingSpinner size="sm" /> Loading attempts...
            </div>
          ) : attempts.length === 0 ? (
            <p className="pt-2 text-sm text-gray-500">
              You have not attempted this quiz yet. Your progress will appear here after submission.
            </p>
          ) : (
            <ul className="mt-3 space-y-3">
              {attempts.map((attempt) => (
                <li key={attempt.id} className="flex flex-wrap items-center gap-3 justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Attempt {attempt.attempt}</Badge>
                      <Badge variant={attempt.status === 'graded' ? 'default' : 'secondary'}>
                        {labelForQuizAttemptStatus(attempt.status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      Submitted:{' '}
                      {attempt.submittedAt
                        ? new Date(attempt.submittedAt).toLocaleString()
                        : 'Not submitted yet'}
                    </p>
                    {attempt.manualScore !== null && attempt.manualScore !== undefined && (
                      <p className="text-xs text-gray-600">
                        Score:{' '}
                        <span className="font-semibold">
                          {attempt.manualScore.toFixed(2)}
                          {quiz.questions
                            ? ` / ${quiz.questions.reduce(
                                (sum, question) => sum + (question.scoreWeight ?? 0),
                                0,
                              )}`
                            : ''}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="inline-flex flex-col gap-2 items-start justify-start">
                    <Button
                      variant="ghost"
                      size="sm"
                      className='px-0'
                      onClick={() => onViewAttempt(attempt.id)}
                    >
                      View details
                    </Button>
                    <p className="text-xs text-gray-600">
                      {attempt.submittedAt
                        ? new Date(attempt.submittedAt).toLocaleString()
                        : 'Not submitted yet'}
                    </p>
                 </div>
                  
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
